import React, { useState, useEffect, useRef } from 'react';
import { Bell, ChevronDown, User, ExternalLink } from 'lucide-react';

/**
 * Company Dashboard Header Component
 * Displays welcome message, search bar, notifications, and company profile
 * Matches the structure and styling of candidate Header
 */
const CompanyHeader: React.FC = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [companyData, setCompanyData] = useState({
    name: '',
    email: '',
    avatar: null as string | null,
    notifications: 0,
    companyId: ''
  });

  // Load company data from localStorage
  useEffect(() => {
    const userDataString = localStorage.getItem('authUser');
    const companyDataString = localStorage.getItem('companyData');

    if (!userDataString) {
      return;
    }

    try {
      const userData = JSON.parse(userDataString);
      let companyId = '';

      // Try to get company ID from companyData in localStorage
      if (companyDataString) {
        try {
          const parsedCompanyData = JSON.parse(companyDataString);
          companyId = parsedCompanyData.id || parsedCompanyData._id || '';
        } catch {
          // Ignore parse errors
        }
      }

      // For company accounts, firstName contains the company name
      // Fallback chain: firstName (company name) -> companyName -> email prefix -> 'Company'
      const companyName = userData.firstName || userData.companyName || userData.email?.split('@')[0] || 'Company';

      setCompanyData({
        name: companyName,
        email: userData.email || '',
        avatar: null,
        notifications: 0,
        companyId: companyId
      });
    } catch (error) {
      console.error('Failed to parse user data:', error);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewPublicProfile = () => {
    if (companyData.companyId) {
      // Open in new tab
      window.open(`/c/${companyData.companyId}`, '_blank');
    }
    setIsDropdownOpen(false);
  };

  return (
    <header className="py-4 lg:py-6 flex items-center justify-between gap-6 bg-transparent transition-all duration-300 mb-4">
      <div className="flex-1 min-w-0">
        <h1 className="text-2xl font-bold text-[#015FA6] font-manrope truncate">
          Welcome back, {companyData.name}!
        </h1>
        <p className="text-sm text-gray-500 mt-1 truncate">
          Manage your job postings and find the best talent for your team.
        </p>
      </div>

      {/* Right Section - Notifications, Profile */}
      <div className="flex items-center gap-4 lg:gap-6 flex-shrink-0">
        {/* Notification Bell */}
        <button className="relative p-2.5 text-gray-500 hover:text-[#015FA6] hover:bg-blue-50 rounded-xl transition-all duration-200 group">
          <Bell className="h-6 w-6" />
          {companyData.notifications > 0 && (
            <span className="absolute top-2 right-2.5 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white transform scale-100 group-hover:scale-110 transition-transform"></span>
          )}
        </button>

        {/* Company Profile */}
        <div className="relative flex items-center gap-3 pl-4 border-l border-gray-200" ref={dropdownRef}>
          <div className="hidden md:block text-right">
            <div className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{companyData.name}</div>
            <div className="text-xs text-gray-500 truncate max-w-[150px]">{companyData.email}</div>
          </div>
          <button
            className="flex items-center gap-2 group"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#015FA6] to-[#6EABFF] p-[2px] shadow-md shadow-blue-500/20 group-hover:shadow-lg group-hover:shadow-blue-500/30 transition-all duration-300">
              <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                {companyData.avatar ? (
                  <img src={companyData.avatar} alt={companyData.name} className="h-full w-full object-cover" />
                ) : (
                  <User className="h-5 w-5 text-[#015FA6]" />
                )}
              </div>
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-400 group-hover:text-[#015FA6] transition-all duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* View Public Profile */}
              <button
                onClick={handleViewPublicProfile}
                disabled={!companyData.companyId}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#015FA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ExternalLink className="h-4 w-4" />
                <span>View Public Profile</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default CompanyHeader;
